/**
 * Leitura do formulário Lead Ads da Meta.
 *
 * A Meta entrega as respostas como uma lista de campos, cada um com um nome
 * definido por quem montou o formulário e uma lista de valores. Alguns nomes
 * são padrão dela e correspondem a campos do Contato; o resto são perguntas
 * próprias, que dizem à Comercial como começar a conversa.
 *
 * Separar as duas coisas é decisão de domínio, não de transporte: é o que
 * define o que vira identidade e o que vira contexto de venda. Por isso mora
 * aqui, longe do cliente HTTP, e é testável sem rede.
 */
/**
 * Nomes que a Meta reserva aos campos padrão.
 *
 * Um formulário pode usar qualquer um, nenhum ou todos. O da Reducee traz
 * apenas `first_name`, `phone_number` e `email` — daí o Contato nascer com o
 * primeiro nome, e não com o nome completo.
 */
const CAMPOS_PADRAO = new Set([
    'full_name',
    'first_name',
    'last_name',
    'email',
    'phone_number',
]);
export function interpretarFormulario(campos) {
    const padrao = new Map();
    const respostas = [];
    for (const campo of campos) {
        // A caixa do nome é escolha de quem montou o formulário, não do contrato.
        const nome = campo.name.trim().toLowerCase();
        const valores = campo.values.map((valor) => valor.trim()).filter((valor) => valor !== '');
        if (CAMPOS_PADRAO.has(nome)) {
            // Campo padrão sem valor continua sendo padrão: não vira pergunta
            // própria só porque a pessoa deixou em branco. E o primeiro valor vence,
            // para que um formulário com campo repetido dê sempre o mesmo Contato.
            if (valores[0] !== undefined && !padrao.has(nome))
                padrao.set(nome, valores[0]);
            continue;
        }
        // Pergunta sem resposta viraria uma linha vazia na tela da Comercial.
        if (valores.length === 0)
            continue;
        respostas.push({ pergunta: campo.name, respostas: valores });
    }
    return {
        contato: {
            nome: resolverNome(padrao),
            telefone: padrao.get('phone_number') ?? null,
            email: padrao.get('email') ?? null,
        },
        respostas,
    };
}
/**
 * O nome completo vence quando existe; senão, nome e sobrenome se juntam.
 *
 * Um formulário pode trazer só um dos dois, e nesse caso o Contato nasce com o
 * que houver. Nunca devolve texto vazio: sem nome nenhum, devolve nulo, para
 * que quem grava decida o que fazer em vez de criar um Contato sem nome.
 */
function resolverNome(padrao) {
    const completo = padrao.get('full_name');
    if (completo !== undefined)
        return completo;
    const partes = [padrao.get('first_name'), padrao.get('last_name')].filter((parte) => parte !== undefined);
    return partes.length > 0 ? partes.join(' ') : null;
}
/**
 * Relê as respostas guardadas no Lead.
 *
 * O campo é Json: nada no banco garante a forma. Uma correção manual, uma
 * versão anterior do código ou uma migração futura podem ter deixado outra
 * coisa ali, e a Visão 360 não pode quebrar por causa disso. Item malformado é
 * descartado sozinho, sem levar embora os que estão bem.
 *
 * Lead de importação ou criado à mão nunca teve formulário: devolve lista
 * vazia, e é ela que faz a tela não mostrar seção nenhuma para ele.
 */
export function lerRespostasGuardadas(bruto) {
    if (!Array.isArray(bruto))
        return [];
    return bruto.flatMap((item) => {
        if (typeof item !== 'object' || item === null)
            return [];
        const resposta = item;
        const pergunta = resposta['pergunta'];
        if (typeof pergunta !== 'string' || pergunta.trim() === '')
            return [];
        const respostas = Array.isArray(resposta['respostas'])
            ? resposta['respostas'].filter((valor) => typeof valor === 'string')
            : [];
        return [{ pergunta, respostas }];
    });
}
/**
 * Torna legível o nome cru que a Meta entrega.
 *
 * O nome do campo é escolhido por quem monta o formulário e chega como
 * `qual_o_seu_plano_atual?`. Mostrar assim à Comercial entregaria identificador
 * de sistema onde deveria estar a pergunta que a pessoa leu.
 *
 * Só troca sublinhado por espaço e sobe a primeira letra: quem já escreveu a
 * pergunta para gente ler não tem o texto mexido.
 *
 * Vale igual para a **resposta** de múltipla escolha, que a Meta normaliza do
 * mesmo jeito — `abaixo_de_r$_1.000,00` é a alternativa que a pessoa marcou,
 * não o que ela digitou. Sublinhado ali é codificação da Meta, não conteúdo.
 */
export function humanizarPergunta(pergunta) {
    const limpo = pergunta.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();
    if (limpo === '')
        return '';
    return limpo.charAt(0).toUpperCase() + limpo.slice(1);
}
/**
 * Casa a pergunta por palavra-chave, e não por igualdade.
 *
 * O marketing cria formulário novo a cada campanha em vez de editar o anterior,
 * e o texto da pergunta vem junto — hoje `quanto_está_investindo_na_mensalidade?`,
 * amanhã outra redação com a mesma intenção. Exigir o nome exato faria o card
 * esvaziar na virada da campanha, sem ninguém entender por quê.
 */
const PISTAS_DA_MENSALIDADE = ['mensalidade', 'investindo', 'paga_hoje'];
const PISTAS_DAS_IDADES = ['idades', 'quem_fará_parte', 'quem_fara_parte'];
function acharPorPista(respostas, pistas) {
    return respostas.find((resposta) => {
        const pergunta = resposta.pergunta.toLowerCase();
        return pistas.some((pista) => pergunta.includes(pista));
    });
}
/**
 * Resume as Respostas do Formulário para o card do funil.
 *
 * **Isto não interpreta para preencher campo.** A ingestão se recusa a derivar
 * tipo de contrato ou valor das respostas, porque errar ali infla o score e
 * desordena a fila Atender Agora — e essa recusa continua de pé. Aqui o texto
 * vai para a tela do jeito que a pessoa escreveu, para a Comercial ler e
 * decidir. Errar o casamento custa uma linha a menos no card, não uma posição
 * roubada na fila.
 *
 * Os dois saem crus, só com o sublinhado da Meta desfeito. A mensalidade chega
 * como `R$ 800`, `800,00`, `em torno de 1.200` ou como alternativa marcada
 * (`abaixo_de_r$_1.000,00`); as idades, em nove formatos diferentes. Normalizar
 * qualquer um dos dois seria adivinhar, e adivinhar aqui erra em silêncio no
 * que a Comercial usa para priorizar.
 */
export function resumirRespostasParaCard(guardadas) {
    const respostas = lerRespostasGuardadas(guardadas);
    const mensalidade = acharPorPista(respostas, PISTAS_DA_MENSALIDADE);
    const idades = acharPorPista(respostas, PISTAS_DAS_IDADES);
    return {
        mensalidadeAtual: primeiraRespostaLegivel(mensalidade),
        idades: primeiraRespostaLegivel(idades),
    };
}
/**
 * A primeira resposta da pergunta, pronta para a tela, ou nulo.
 *
 * Nulo tanto para pergunta ausente quanto para respondida em branco: as duas
 * querem dizer "ninguém disse isto", e no card as duas somem. Traço ou zero no
 * lugar do vazio se leriam como resposta.
 *
 * A quebra de linha vira espaço porque há quem responda uma idade por linha, e
 * numa linha só de card isso viraria texto grudado.
 */
function primeiraRespostaLegivel(resposta) {
    const bruta = resposta?.respostas[0];
    if (bruta === undefined)
        return null;
    return humanizarPergunta(bruta.replace(/\s+/g, ' ')).trim() || null;
}
//# sourceMappingURL=meta-lead-ads.js.map